import { useState, useRef, ChangeEvent } from 'react';
import { toast } from 'react-toastify';
import { getApiUrl, getApiHeadersAsync } from '../utils/api';
import { compressImagesForUpload, compressImagesForShopify, estimateUploadTooLarge } from '../utils/compressImage';
import './CreateListingPage.css';


interface ExtractedData {
    title: string;
    description: string;
    bodyHtml?: string;
    shortDescription: string;
    materials: string[];
    dimensions: {
        width: string;
        height: string;
        depth: string;
        weight: string;
    };
    colors: string[];
    style: string;
    category: string;
    tags: string[];
    suggestedPrice: {
        min: number;
        max: number;
        currency: string;
    };
    listingPrice: number | '';
    alt_text?: string;
    metafields?: Array<{ namespace: string; key: string; value: string; type: string }>;
    seo?: { title?: string; description?: string };
    confidence: number;
}

interface ImageFile {
    file: File;
    preview: string;
}

type Step = 'upload' | 'processing' | 'review' | 'enhance' | 'motion' | 'publish';

interface GeneratedImage {
    type: string;
    image: string;
    mimeType: string;
    altText?: string;
}

interface MotionLanguageContent {
    locale: string;
    flag: string;
    languageName: string;
    title: string;
    sellingPoints: string[];
    dimensions: string;
    units: 'metric' | 'imperial';
}

const LANG_META: Record<string, { flag: string; name: string; units: 'metric' | 'imperial' }> = {
    'en-US': { flag: '🇺🇸', name: 'English (US)', units: 'imperial' },
    'en-GB': { flag: '🇬🇧', name: 'English (UK)', units: 'imperial' },
    'fr-FR': { flag: '🇫🇷', name: 'Français', units: 'metric' },
    'de-DE': { flag: '🇩🇪', name: 'Deutsch', units: 'metric' },
    'es-ES': { flag: '🇪🇸', name: 'Español', units: 'metric' },
    'pt-PT': { flag: '🇵🇹', name: 'Português', units: 'metric' },
    'it-IT': { flag: '🇮🇹', name: 'Italiano', units: 'metric' },
    'nl-NL': { flag: '🇳🇱', name: 'Nederlands', units: 'metric' },
    'ja-JP': { flag: '🇯🇵', name: '日本語', units: 'metric' },
    'ar-SA': { flag: '🇸🇦', name: 'العربية', units: 'metric' },
};

// Convert metric dimensions string for imperial markets
function convertDimensionsForLocale(dimensions: ExtractedData['dimensions'], units: 'metric' | 'imperial'): string {
    const parse = (val: string) => parseFloat(val.replace(/[^0-9.]/g, ''));
    const w = parse(dimensions.width || '0');
    const h = parse(dimensions.height || '0');
    const d = parse(dimensions.depth || '0');
    const wt = parse(dimensions.weight || '0');

    if (units === 'imperial') {
        const toIn = (cm: number) => cm > 0 ? `${(cm / 2.54).toFixed(1)}"` : null;
        const toLb = (kg: number) => kg > 0 ? `${(kg * 2.205).toFixed(1)} lb` : null;
        const parts = [toIn(w), toIn(h), toIn(d)].filter(Boolean).join(' × ');
        const weight = toLb(wt);
        return [parts, weight].filter(Boolean).join(', ');
    } else {
        const toCm = (v: number, orig: string) => v > 0 ? `${v}${orig.includes('cm') ? 'cm' : orig.includes('mm') ? 'mm' : 'cm'}` : null;
        const toKg = (v: number, orig: string) => v > 0 ? `${v}${orig.includes('kg') ? 'kg' : 'g'}` : null;
        const parts = [toCm(w, dimensions.width || ''), toCm(h, dimensions.height || ''), toCm(d, dimensions.depth || '')].filter(Boolean).join(' × ');
        const weight = toKg(wt, dimensions.weight || '');
        return [parts, weight].filter(Boolean).join(', ');
    }
}

function CreateListingPage() {
    const [step, setStep] = useState<Step>('upload');
    const [images, setImages] = useState<ImageFile[]>([]);
    const [extractedData, setExtractedData] = useState<ExtractedData | null>(null);
    const [useAI, setUseAI] = useState(true);
    const [generateStudioImages, setGenerateStudioImages] = useState(true);
    const [selectedMarketplaces, setSelectedMarketplaces] = useState<string[]>(['shopify']);
    const [generatedImages, setGeneratedImages] = useState<GeneratedImage[]>([]);
    const [isGeneratingImages, setIsGeneratingImages] = useState(false);
    const [imageGenError, setImageGenError] = useState<string | null>(null);
    const [isPublishing, setIsPublishing] = useState(false);
    const [publishStatus, setPublishStatus] = useState<'idle' | 'publishing' | 'success' | 'error'>('idle');
    const [generateMotionImages, setGenerateMotionImages] = useState(false);
    const [targetLanguages] = useState<string[]>(() => {
        try {
            const saved = localStorage.getItem('commerium_target_languages');
            return saved ? JSON.parse(saved) : ['en-US'];
        } catch { return ['en-US']; }
    });
    const [motionContent, setMotionContent] = useState<MotionLanguageContent[]>([]);
    const [isGeneratingMotion, setIsGeneratingMotion] = useState(false);
    const [editablePoints, setEditablePoints] = useState<Record<string, string[]>>({});

    const fileInputRef = useRef<HTMLInputElement>(null);
    const cameraInputRef = useRef<HTMLInputElement>(null);

    const safeTags = (tags: unknown): string[] => {
        if (Array.isArray(tags)) return tags;
        if (typeof tags === 'string') return tags.split(',').map(t => t.trim()).filter(Boolean);
        return [];
    };

    const safeMaterials = (materials: unknown): string[] => {
        if (Array.isArray(materials)) return materials;
        if (typeof materials === 'string') return materials.split(',').map(m => m.trim()).filter(Boolean);
        return [];
    };

    const handleFileSelect = async (e: ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files) return;

        try {
            const compressed = await compressImagesForUpload(Array.from(files));
            const newImages = compressed.map((file) => ({
                file,
                preview: URL.createObjectURL(file),
            }));
            setImages((prev) => [...prev, ...newImages]);
        } catch (error) {
            console.error('Image compression failed:', error);
            toast.error('Could not prepare images. Try a smaller photo.');
        } finally {
            e.target.value = '';
        }
    };

    const removeImage = (index: number) => {
        setImages((prev) => {
            const updated = [...prev];
            URL.revokeObjectURL(updated[index].preview);
            updated.splice(index, 1);
            return updated;
        });
    };

    const processImages = async () => {
        if (images.length === 0) {
            toast.error('Please upload at least one image');
            return;
        }

        setStep('processing');

        try {
            const files = await compressImagesForUpload(images.map((img) => img.file));
            if (estimateUploadTooLarge(files)) {
                toast.error('Photos are still too large. Use fewer images or smaller photos.');
                setStep('upload');
                return;
            }

            const formData = new FormData();
            files.forEach((file) => {
                formData.append('images', file);
            });
            formData.append('generateStudioImages', String(generateStudioImages));
            formData.append('targetMarketplaces', JSON.stringify(selectedMarketplaces));
            formData.append('skipAI', String(!useAI));

            const headers: Record<string, string> = {
                ...(await getApiHeadersAsync()),
            };
            delete headers['Content-Type'];

            const response = await fetch(getApiUrl('/api/listings/create-from-photo'), {
                method: 'POST',
                headers,
                body: formData
            });

            if (!response.ok) {
                const errBody = await response.json().catch(() => ({}));
                if (response.status === 413) {
                    throw new Error('Photos are too large for upload. Try one photo or a smaller image.');
                }
                if (response.status === 503 || response.status === 502) {
                    throw new Error(errBody.details || errBody.error || 'AI extraction failed. Please try again.');
                }
                throw new Error(errBody.details || errBody.error || 'Failed to process images');
            }

            const data = await response.json();
            const rawData = data.listing.extractedData;

            const normalizedData: ExtractedData = {
                title: rawData.title || '',
                description: rawData.description || '',
                bodyHtml: rawData.body_html || rawData.description || '',
                shortDescription: rawData.shortDescription || rawData.seo?.description || '',
                materials: Array.isArray(rawData.materials) ? rawData.materials : [],
                dimensions: rawData.dimensions || { width: '', height: '', depth: '', weight: '' },
                colors: Array.isArray(rawData.colors) ? rawData.colors : [],
                style: rawData.style || '',
                category: rawData.category || rawData.product_type || '',
                tags: Array.isArray(rawData.tags)
                    ? rawData.tags
                    : (typeof rawData.tags === 'string' ? rawData.tags.split(',').map((t: string) => t.trim()) : []),
                suggestedPrice: rawData.suggestedPrice || { min: 0, max: 0, currency: 'EUR' },
                listingPrice: (() => {
                    const detected = rawData.variants?.[0]?.price ?? rawData.suggestedPrice?.min;
                    const parsed = parseFloat(String(detected ?? ''));
                    return Number.isFinite(parsed) && parsed > 0 ? parsed : '';
                })(),
                alt_text: rawData.alt_text || '',
                metafields: rawData.metafields || [],
                seo: rawData.seo || {},
                confidence: rawData.confidence || 0.85,
            };

            setExtractedData(normalizedData);
            setStep('review');
            toast.success('Product data extracted successfully!');
        } catch (error) {
            console.error('Processing error:', error);
            toast.error(error instanceof Error ? error.message : 'Failed to process images. Please try again.');
            setStep('upload');
        }
    };

    const marketplaces = [
        { id: 'shopify', name: 'Shopify', icon: '🛒' },
        { id: 'etsy', name: 'Etsy', icon: '🎨' },
        { id: 'amazon', name: 'Amazon', icon: '📦' },
        { id: 'pinterest', name: 'Pinterest', icon: '📌' },
        { id: 'ebay', name: 'eBay', icon: '🏷️' }
    ];

    const toggleMarketplace = (id: string) => {
        setSelectedMarketplaces((prev) =>
            prev.includes(id)
                ? prev.filter((m) => m !== id)
                : [...prev, id]
        );
    };

    const handleGoToEnhance = async () => {
        if (!extractedData) return;

        if (extractedData.listingPrice === '' || Number(extractedData.listingPrice) <= 0) {
            toast.error('Please set a listing price before continuing.');
            return;
        }

        if (!generateStudioImages || images.length === 0) {
            setStep('publish');
            return;
        }

        setStep('enhance');
        setIsGeneratingImages(true);
        setImageGenError(null);
        setGeneratedImages([]);

        try {
            const [compressed] = await compressImagesForUpload([images[0].file]);
            const formData = new FormData();
            formData.append('image', compressed);
            formData.append('angles', 'multiple');
            if (extractedData.title) {
                formData.append('productTitle', extractedData.title);
            }
            if (extractedData.category) {
                formData.append('productType', extractedData.category);
            }
            if (extractedData.description) {
                formData.append('productDescription', extractedData.description);
            }

            const headers: Record<string, string> = {
                ...(await getApiHeadersAsync()),
            };
            delete headers['Content-Type'];

            const response = await fetch(getApiUrl('/api/listings/generate-studio-images'), {
                method: 'POST',
                headers,
                body: formData
            });

            const data = await response.json().catch(() => ({}));

            if (!response.ok) {
                throw new Error(data.details || data.error || 'Failed to generate images');
            }

            if (data.success && data.images) {
                const generated: GeneratedImage[] = data.images
                    .filter((img: { success: boolean }) => img.success)
                    .map((img: { angle?: string; image: string; mimeType: string; altText?: string }) => ({
                        type: img.angle || 'studio',
                        image: img.image,
                        mimeType: img.mimeType || 'image/png',
                        altText: img.altText || ''
                    }));

                setGeneratedImages(generated);

                if (generated.length > 0) {
                    toast.success(`Generated ${generated.length} studio images!`);
                } else {
                    const firstError = data.images?.find((img: { error?: string }) => img.error)?.error;
                    const message = firstError || 'No images were generated. Please try again.';
                    setImageGenError(message);
                    toast.warning('No images were generated. Using originals.');
                }
            }
        } catch (error) {
            console.error('Image generation error:', error);
            const message = error instanceof Error ? error.message : 'Failed to generate images';
            setImageGenError(message);
            toast.error('Failed to generate images. Using originals.');
        } finally {
            setIsGeneratingImages(false);
        }
    };

    const handleGoToMotion = async () => {
        if (!extractedData || !generateMotionImages || targetLanguages.length === 0) {
            setStep('publish');
            return;
        }

        setStep('motion');
        setIsGeneratingMotion(true);
        setMotionContent([]);


        let translatedContent: MotionLanguageContent[] = [];

        // ── Step 1: Gemini translation ────────────────────────────────────────
        try {
            const headers: Record<string, string> = {
                ...(await getApiHeadersAsync()),
                'Content-Type': 'application/json',
            };
            const res = await fetch(getApiUrl('/api/listings/generate-motion-content'), {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    title: extractedData.title,
                    description: extractedData.description,
                    dimensions: extractedData.dimensions,
                    materials: extractedData.materials,
                    locales: targetLanguages
                })
            });
            if (!res.ok) throw new Error('Translate failed');
            const data = await res.json();
            if (data.success && data.content) {
                translatedContent = data.content;
            } else throw new Error('No content returned');
        } catch (translateError) {
            console.warn('Translation failed, using fallback', translateError);
            translatedContent = targetLanguages.map(locale => {
                const meta = LANG_META[locale] || { flag: '🌐', name: locale, units: 'metric' as const };
                return {
                    locale, flag: meta.flag, languageName: meta.name,
                    title: extractedData.title,
                    sellingPoints: [
                        Array.isArray(extractedData.materials) ? extractedData.materials.slice(0, 2).join(' & ') || 'Handmade quality' : 'Handmade quality',
                        `${extractedData.style || 'Artisan'} design`,
                        extractedData.category || 'One-of-a-kind piece'
                    ],
                    dimensions: convertDimensionsForLocale(extractedData.dimensions, meta.units),
                    units: meta.units
                };
            });
        }

        // Initialise editable points
        setMotionContent(translatedContent);
        const initPts: Record<string, string[]> = {};
        translatedContent.forEach(c => { initPts[c.locale] = [...c.sellingPoints]; });
        setEditablePoints(initPts);

        setIsGeneratingMotion(false);
    };



    // Open the rendered HTML post card in a print window for download
    const downloadMotionCard = (locale: string, title: string) => {
        const card = document.getElementById(`motion-card-${locale}`);
        if (!card) return;
        const printWindow = window.open('', '_blank');
        if (!printWindow) return;
        const styles = Array.from(document.styleSheets)
            .map(s => { try { return Array.from(s.cssRules).map(r => r.cssText).join('\n'); } catch { return ''; } })
            .join('\n');
        printWindow.document.write(`<html><head><style>${styles}</style></head><body>${card.outerHTML}</body></html>`);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => { printWindow.print(); printWindow.close(); }, 400);
        toast.success(`🖥️ Opened ${title} for download/print`);
    };

    const publishListing = async () => {
        if (!extractedData) {
            toast.error('No listing data to publish');
            return;
        }

        if (extractedData.listingPrice === '' || Number(extractedData.listingPrice) <= 0) {
            toast.error('Please set a listing price before publishing.');
            return;
        }

        setIsPublishing(true);
        setPublishStatus('publishing');

        try {
            const formData = new FormData();

            formData.append('title', extractedData.title);
            formData.append('description', extractedData.description);
            formData.append('bodyHtml', extractedData.bodyHtml || extractedData.description);
            formData.append('category', extractedData.category);
            formData.append('style', extractedData.style);
            formData.append('tags', JSON.stringify(extractedData.tags));
            formData.append('materials', JSON.stringify(extractedData.materials));
            formData.append('metafields', JSON.stringify(extractedData.metafields || []));
            if (extractedData.seo?.title) {
                formData.append('seoTitle', extractedData.seo.title);
            }
            if (extractedData.seo?.description || extractedData.shortDescription) {
                formData.append('seoDescription', extractedData.seo?.description || extractedData.shortDescription);
            }
            formData.append('price', String(
                Number(extractedData.listingPrice) > 0
                    ? extractedData.listingPrice
                    : extractedData.suggestedPrice.min || 0
            ));
            formData.append('marketplaces', JSON.stringify(selectedMarketplaces));

            if (generatedImages.length > 0) {
                const altTextsArr: string[] = [];
                const generatedFiles = generatedImages.map((img, index) => {
                    const byteString = atob(img.image);
                    const ab = new ArrayBuffer(byteString.length);
                    const ia = new Uint8Array(ab);
                    for (let i = 0; i < byteString.length; i++) {
                        ia[i] = byteString.charCodeAt(i);
                    }
                    const blob = new Blob([ab], { type: img.mimeType });
                    return new File([blob], `generated_${index}.png`, { type: img.mimeType });
                });
                const compressedGenerated = await compressImagesForShopify(generatedFiles);
                compressedGenerated.forEach((file, index) => {
                    formData.append('images', file);
                    altTextsArr.push(generatedImages[index].altText || extractedData.title);
                });
                formData.append('altTexts', JSON.stringify(altTextsArr));
            } else {
                const altTextsArr: string[] = [];
                const compressedPhotos = await compressImagesForShopify(images.map((img) => img.file));
                compressedPhotos.forEach((file, index) => {
                    formData.append('images', file);
                    // Use extracted alt_text for the first image, title for others
                    altTextsArr.push(index === 0 ? (extractedData.alt_text || extractedData.title) : extractedData.title);
                });
                formData.append('altTexts', JSON.stringify(altTextsArr));
            }

            const headers: Record<string, string> = {
                ...(await getApiHeadersAsync()),
            };
            delete headers['Content-Type'];

            const response = await fetch(getApiUrl('/api/listings/publish'), {
                method: 'POST',
                headers,
                body: formData
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || 'Failed to publish listing');
            }

            await response.json();
            setPublishStatus('success');
            toast.success('🎉 Listing published successfully!');

            setTimeout(() => {
                setStep('upload');
                setImages([]);
                setExtractedData(null);
                setGeneratedImages([]);
                setPublishStatus('idle');
            }, 2000);

        } catch (error) {
            console.error('Publish error:', error);
            setPublishStatus('error');
            toast.error(error instanceof Error ? error.message : 'Failed to publish listing');
        } finally {
            setIsPublishing(false);
        }
    };

    return (
        <div className="create-listing-page animate-fade-in">
            <h1 className="page-title">
                <span className="title-icon">📸</span>
                Create Listing
            </h1>
            <p className="page-subtitle">
                Take photos of your handmade product and we'll create a professional listing
            </p>

            {/* Step Indicator */}
            <div className="step-indicator">
                {([
                    'upload', 'processing', 'review', 'enhance',
                    ...(generateMotionImages ? ['motion'] : []),
                    'publish'
                ] as Step[]).map((s, i, arr) => (
                    <div key={s} className={`step ${step === s ? 'active' : ''} ${arr.indexOf(step) > i ? 'completed' : ''}`}>
                        <span className="step-number">{i + 1}</span>
                        <span className="step-label">{s === 'motion' ? '🌍 Intl' : s.charAt(0).toUpperCase() + s.slice(1)}</span>
                    </div>
                ))}
            </div>

            {/* Upload Step */}
            {step === 'upload' && (
                <div
                    className="upload-section animate-slide-up"
                    onDragOver={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                    }}
                    onDrop={(e) => {
                        e.preventDefault();
                        e.stopPropagation();

                        const files = e.dataTransfer.files;
                        if (!files || files.length === 0) return;

                        const newImages = Array.from(files).map((file) => ({
                            file,
                            preview: URL.createObjectURL(file)
                        }));

                        setImages((prev) => [...prev, ...newImages]);
                    }}
                >
                    <div className="upload-zone-hint">
                        {images.length === 0 ? 'Drag & Drop photos here or using buttons below' : 'Drag & Drop more photos'}
                    </div>

                    <div className="camera-buttons">
                        <button
                            className="btn btn-primary btn-large camera-btn"
                            onClick={() => cameraInputRef.current?.click()}
                        >
                            <span>📷</span>
                            Take Photo
                        </button>
                        <button
                            className="btn btn-secondary btn-large"
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <span>🖼️</span>
                            Upload Image
                        </button>
                    </div>

                    <input
                        ref={cameraInputRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        onChange={handleFileSelect}
                        style={{ display: 'none' }}
                        multiple
                    />
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleFileSelect}
                        style={{ display: 'none' }}
                        multiple
                    />

                    {images.length > 0 && (
                        <div className="image-preview-grid">
                            {images.map((img, index) => (
                                <div key={index} className="image-preview-item">
                                    <img src={img.preview} alt={`Preview ${index + 1}`} />
                                    <button
                                        className="remove-image-btn"
                                        onClick={() => removeImage(index)}
                                    >
                                        ×
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    {images.length > 0 && (
                        <div className="options-section">
                            <h3 className="options-title">Advanced Processing</h3>
                            <div className="options-grid">
                                <div
                                    className={`option-card ${useAI ? 'active' : ''}`}
                                    onClick={() => {
                                        setUseAI(!useAI);
                                        if (useAI) setGenerateStudioImages(false);
                                    }}
                                >
                                    <div className="option-card-header">
                                        <div className="option-card-icon">✨</div>
                                        <div className="option-card-toggle">
                                            <div className="option-toggle-knob"></div>
                                        </div>
                                    </div>
                                    <div className="option-card-content">
                                        <div className="option-card-label">AI Analysis</div>
                                        <div className="option-card-desc">Extract all product details automatically with AI</div>
                                    </div>
                                </div>

                                <div
                                    className={`option-card ${generateStudioImages ? 'active' : ''} ${!useAI ? 'disabled' : ''}`}
                                    onClick={() => useAI && setGenerateStudioImages(!generateStudioImages)}
                                >
                                    <div className="option-card-header">
                                        <div className="option-card-icon">📸</div>
                                        <div className="option-card-toggle">
                                            <div className="option-toggle-knob"></div>
                                        </div>
                                    </div>
                                    <div className="option-card-content">
                                        <div className="option-card-label">Studio Images</div>
                                        <div className="option-card-desc">Generate professional white-background studio photos</div>
                                    </div>
                                </div>

                                {/* Motion Images card (optional) */}
                                <div
                                    className={`option-card motion-option-card ${generateMotionImages ? 'active' : ''} ${!useAI ? 'disabled' : ''}`}
                                    onClick={() => useAI && setGenerateMotionImages(!generateMotionImages)}
                                >
                                    <div className="option-card-header">
                                        <div className="option-card-icon">🌍</div>
                                        <div className="option-card-toggle">
                                            <div className="option-toggle-knob"></div>
                                        </div>
                                    </div>
                                    <div className="option-card-content">
                                        <div className="option-card-label">
                                            Motion Images
                                            <span className="motion-option-badge">NEW</span>
                                        </div>
                                        <div className="option-card-desc">
                                            Generate multilingual social posts with selling-point text overlays for each target market
                                        </div>
                                        {generateMotionImages && targetLanguages.length > 0 && (
                                            <div className="motion-option-langs">
                                                {targetLanguages.slice(0, 5).map(l => {
                                                    const m = ({ 'en-US': '🇺🇸', 'en-GB': '🇬🇧', 'fr-FR': '🇫🇷', 'de-DE': '🇩🇪', 'es-ES': '🇪🇸', 'pt-PT': '🇵🇹', 'it-IT': '🇮🇹', 'nl-NL': '🇳🇱', 'ja-JP': '🇯🇵', 'ar-SA': '🇸🇦' } as Record<string, string>);
                                                    return <span key={l}>{m[l] || '🌐'}</span>;
                                                })}
                                                {targetLanguages.length > 5 && <span>+{targetLanguages.length - 5}</span>}
                                            </div>
                                        )}
                                        {generateMotionImages && targetLanguages.length === 0 && (
                                            <div className="motion-option-warning">
                                                ⚠ Add markets in Settings → Target Markets
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="marketplace-selection">
                                <h4>Target Marketplaces</h4>
                                <div className="marketplace-grid">
                                    {marketplaces.map((mp) => (
                                        <button
                                            key={mp.id}
                                            className={`marketplace-btn ${selectedMarketplaces.includes(mp.id) ? 'selected' : ''}`}
                                            onClick={() => toggleMarketplace(mp.id)}
                                        >
                                            <span className="mp-icon">{mp.icon}</span>
                                            <span className="mp-name">{mp.name}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <button
                                className="btn btn-primary btn-large process-btn"
                                onClick={processImages}
                            >
                                <span>✨</span>
                                Create Listing with AI
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* Processing Step */}
            {step === 'processing' && (
                <div className="processing-section animate-fade-in">
                    <div className="processing-animation">
                        <div className="loader-ring"></div>
                        <span className="processing-icon">🎨</span>
                    </div>
                    <h2>Creating Your Listing...</h2>
                    <p className="processing-status">
                        Extracting product details and generating descriptions
                    </p>
                    <div className="processing-steps">
                        <div className="processing-step active">
                            <span className="check">✓</span> Analyzing images
                        </div>
                        <div className="processing-step active">
                            <span className="check">✓</span> Extracting materials and dimensions
                        </div>
                        <div className="processing-step pulse">
                            <span className="spinner">⟳</span> Generating descriptions
                        </div>
                        <div className="processing-step">
                            <span className="pending">○</span> Optimizing for marketplaces
                        </div>
                    </div>
                </div>
            )}

            {/* Review Step */}
            {step === 'review' && extractedData && (
                <div className="review-section animate-slide-up">
                    {/* Show uploaded images */}
                    {images.length > 0 && (
                        <div className="review-images">
                            <h3>Your Product Photos</h3>
                            <div className="image-preview-grid">
                                {images.map((img, index) => (
                                    <div key={index} className="image-preview-item">
                                        <img src={img.preview} alt={`Product ${index + 1}`} />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="extracted-data-card card">
                        <div className="confidence-badge">
                            <span className="confidence-label">AI Confidence</span>
                            <span className="confidence-value">{Math.round(extractedData.confidence * 100)}%</span>
                        </div>

                        <div className="form-group">
                            <label>Title</label>
                            <input
                                type="text"
                                value={extractedData.title}
                                onChange={(e) => setExtractedData({ ...extractedData, title: e.target.value })}
                            />
                        </div>

                        <div className="form-group">
                            <label>Description</label>
                            <textarea
                                rows={4}
                                value={extractedData.description}
                                onChange={(e) => setExtractedData({ ...extractedData, description: e.target.value })}
                            />
                        </div>

                        <div className="form-group">
                            <label>Image Alt Text (SEO)</label>
                            <input
                                type="text"
                                placeholder="Describe the image for SEO and accessibility"
                                value={extractedData.alt_text || ''}
                                onChange={(e) => setExtractedData({ ...extractedData, alt_text: e.target.value })}
                            />
                            <small className="form-help">Better alt text improves your search ranking on Google and Etsy.</small>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>Category</label>
                                <input
                                    type="text"
                                    value={extractedData.category}
                                    onChange={(e) => setExtractedData({ ...extractedData, category: e.target.value })}
                                />
                            </div>
                            <div className="form-group">
                                <label>Style</label>
                                <input
                                    type="text"
                                    value={extractedData.style}
                                    onChange={(e) => setExtractedData({ ...extractedData, style: e.target.value })}
                                />
                            </div>
                        </div>

                        {/* Dimensions & Weight Section */}
                        <div className="dimensions-section">
                            <h4>📐 Dimensions & Weight</h4>
                            <div className="dimensions-grid">
                                <div className="dimension-item">
                                    <label>Width</label>
                                    <input
                                        type="text"
                                        value={extractedData.dimensions?.width || ''}
                                        onChange={(e) => setExtractedData({
                                            ...extractedData,
                                            dimensions: { ...extractedData.dimensions, width: e.target.value }
                                        })}
                                    />
                                </div>
                                <div className="dimension-item">
                                    <label>Height</label>
                                    <input
                                        type="text"
                                        value={extractedData.dimensions?.height || ''}
                                        onChange={(e) => setExtractedData({
                                            ...extractedData,
                                            dimensions: { ...extractedData.dimensions, height: e.target.value }
                                        })}
                                    />
                                </div>
                                <div className="dimension-item">
                                    <label>Depth</label>
                                    <input
                                        type="text"
                                        value={extractedData.dimensions?.depth || ''}
                                        onChange={(e) => setExtractedData({
                                            ...extractedData,
                                            dimensions: { ...extractedData.dimensions, depth: e.target.value }
                                        })}
                                    />
                                </div>
                                <div className="dimension-item">
                                    <label>⚖️ Weight</label>
                                    <input
                                        type="text"
                                        value={extractedData.dimensions?.weight || ''}
                                        onChange={(e) => setExtractedData({
                                            ...extractedData,
                                            dimensions: { ...extractedData.dimensions, weight: e.target.value }
                                        })}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Materials</label>
                            <input
                                type="text"
                                placeholder="e.g. wood, linen, ceramic"
                                value={safeMaterials(extractedData.materials).join(', ')}
                                onChange={(e) => setExtractedData({
                                    ...extractedData,
                                    materials: e.target.value.split(',').map((m) => m.trim()).filter(Boolean),
                                })}
                            />
                        </div>

                        <div className="form-group">
                            <label>Listing Price</label>
                            <div className="price-input-row">
                                <select
                                    value={extractedData.suggestedPrice.currency}
                                    onChange={(e) => setExtractedData({
                                        ...extractedData,
                                        suggestedPrice: {
                                            ...extractedData.suggestedPrice,
                                            currency: e.target.value,
                                        },
                                    })}
                                >
                                    <option value="EUR">EUR</option>
                                    <option value="USD">USD</option>
                                    <option value="GBP">GBP</option>
                                </select>
                                <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    placeholder="Enter your price"
                                    value={extractedData.listingPrice}
                                    onChange={(e) => setExtractedData({
                                        ...extractedData,
                                        listingPrice: e.target.value === '' ? '' : Number(e.target.value),
                                    })}
                                />
                            </div>
                            {(extractedData.suggestedPrice.min > 0 || extractedData.suggestedPrice.max > 0) && (
                                <p className="price-hint">
                                    AI suggested range: {extractedData.suggestedPrice.currency}{' '}
                                    {extractedData.suggestedPrice.min} – {extractedData.suggestedPrice.max}
                                </p>
                            )}
                        </div>

                        <div className="form-group">
                            <label>Tags</label>
                            <input
                                type="text"
                                placeholder="handmade, artisan, lamp, home decor"
                                value={safeTags(extractedData.tags).join(', ')}
                                onChange={(e) => setExtractedData({
                                    ...extractedData,
                                    tags: e.target.value.split(',').map((t) => t.trim()).filter(Boolean),
                                })}
                            />
                        </div>
                    </div>

                    <div className="action-buttons">
                        <button
                            className="btn btn-secondary"
                            onClick={() => setStep('upload')}
                        >
                            ← Back
                        </button>
                        <button
                            className="btn btn-primary"
                            onClick={handleGoToEnhance}
                        >
                            Continue to Enhance →
                        </button>
                    </div>
                </div>
            )}

            {/* Enhance Step */}
            {step === 'enhance' && (
                <div className="enhance-section animate-slide-up">
                    {isGeneratingImages ? (
                        <div className="generating-images">
                            <div className="processing-animation">
                                <div className="loader-ring"></div>
                                <span className="processing-icon">🎨</span>
                            </div>
                            <h2>Generating images...</h2>
                            <p>Creating professional product photos</p>
                            <div className="generating-progress">
                                <div className="progress-item active">
                                    <span>📷</span> Studio shot
                                </div>
                                <div className="progress-item">
                                    <span>🏠</span> Lifestyle image
                                </div>
                                <div className="progress-item">
                                    <span>🔍</span> Detail close-up
                                </div>
                            </div>
                        </div>
                    ) : generatedImages.length > 0 ? (
                        <>
                            <div className="card">
                                <h3>🎨 Generated Product Images</h3>
                                <p>AI-enhanced professional product photography</p>
                                <div className="generated-images-grid">
                                    {generatedImages.map((img, index) => (
                                        <div key={index} className="generated-image-item">
                                            <img
                                                src={`data:${img.mimeType};base64,${img.image}`}
                                                alt={`Generated ${img.type}`}
                                            />
                                            <span className="image-label">{img.type}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="card">
                                <h3>📸 Original Photos</h3>
                                <div className="image-preview-grid small">
                                    {images.map((img, index) => (
                                        <div key={index} className="image-preview-item">
                                            <img src={img.preview} alt={`Original ${index + 1}`} />
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="action-buttons">
                                <button
                                    className="btn btn-secondary"
                                    onClick={() => setStep('review')}
                                >
                                    ← Back
                                </button>
                                <button
                                    className="btn btn-primary"
                                    onClick={handleGoToMotion}
                                >
                                    {generateMotionImages ? 'Continue to Intl Posts →' : 'Accept & Publish →'}
                                </button>
                            </div>
                        </>
                    ) : (
                        <div className="card">
                            <h3>⚠️ No Images Generated</h3>
                            <p>There was an issue generating images. You can still proceed with your original photos.</p>
                            {imageGenError && <p className="image-gen-error">{imageGenError}</p>}
                            <div className="image-preview-grid">
                                {images.map((img, index) => (
                                    <div key={index} className="image-preview-item">
                                        <img src={img.preview} alt={`Product ${index + 1}`} />
                                    </div>
                                ))}
                            </div>
                            <div className="action-buttons">
                                <button
                                    className="btn btn-secondary"
                                    onClick={() => handleGoToEnhance()}
                                >
                                    🔄 Try Again
                                </button>
                                <button
                                    className="btn btn-primary"
                                    onClick={handleGoToMotion}
                                >
                                    {generateMotionImages ? 'Continue to Intl Posts →' : 'Continue with Originals →'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Motion / International Posts Step */}
            {step === 'motion' && (
                <div className="motion-section animate-slide-up">
                    {isGeneratingMotion ? (
                        <div className="generating-images">
                            <div className="processing-animation">
                                <div className="loader-ring"></div>
                                <span className="processing-icon">🌍</span>
                            </div>
                            <h2>Translating for {targetLanguages.length} Market{targetLanguages.length > 1 ? 's' : ''}…</h2>
                            <p>AI is generating selling points and localising measurements</p>
                            <div className="generating-progress">
                                {targetLanguages.map((loc) => {
                                    const flags: Record<string, string> = { 'en-US': '🇺🇸', 'en-GB': '🇬🇧', 'fr-FR': '🇫🇷', 'de-DE': '🇩🇪', 'es-ES': '🇪🇸', 'pt-PT': '🇵🇹', 'it-IT': '🇮🇹', 'nl-NL': '🇳🇱', 'ja-JP': '🇯🇵', 'ar-SA': '🇸🇦' };
                                    return <div key={loc} className="progress-item active"><span>{flags[loc] || '🌐'}</span>{loc}</div>;
                                })}
                            </div>
                        </div>
                    ) : motionContent.length > 0 ? (
                        <>
                            <div className="card motion-header-card">
                                <div className="motion-header">
                                    <div>
                                        <h3>🌍 International Posts Ready</h3>
                                        <p>
                                            {motionContent.length} language{motionContent.length > 1 ? 's' : ''} — edit selling points, then download
                                        </p>
                                    </div>
                                    <button
                                        className="btn btn-secondary motion-download-all-btn"
                                        onClick={() => motionContent.forEach(c => {
                                            downloadMotionCard(c.locale, c.languageName);
                                        })}
                                    >
                                        ⬇ Download All
                                    </button>
                                </div>
                            </div>

                            <div className="motion-cards-row">
                                {motionContent.map((content) => {
                                    const pts = editablePoints[content.locale] || content.sellingPoints;
                                    const bgImg = images[0]?.preview;
                                    return (
                                        <div key={content.locale} className="motion-card-wrapper">
                                            <div
                                                id={`motion-card-${content.locale}`}
                                                className="motion-card"
                                                style={bgImg ? { backgroundImage: `url(${bgImg})` } : {}}
                                            >
                                                <div className="motion-card-overlay">
                                                    <div className="motion-card-title">{content.title}</div>
                                                    <ul className="motion-card-points">
                                                        {pts.map((pt, i) => (
                                                            <li key={i}>✦ {pt}</li>
                                                        ))}
                                                    </ul>
                                                    {content.dimensions && (
                                                        <div className="motion-card-dims">📐 {content.dimensions}</div>
                                                    )}
                                                </div>
                                                <div className="motion-card-badge">
                                                    <span className="motion-card-flag">{content.flag}</span>
                                                    <span className="motion-card-lang">{content.languageName}</span>
                                                </div>
                                            </div>

                                            {/* Controls */}
                                            <div className="motion-card-controls">
                                                <div className="motion-controls-label">Edit selling points:</div>
                                                {pts.map((pt, i) => (
                                                    <input
                                                        key={i}
                                                        type="text"
                                                        className="motion-point-input"
                                                        value={pt}
                                                        onChange={(e) => {
                                                            const updated = [...pts];
                                                            updated[i] = e.target.value;
                                                            setEditablePoints(prev => ({ ...prev, [content.locale]: updated }));
                                                        }}
                                                        placeholder={`Selling point ${i + 1}`}
                                                    />
                                                ))}
                                                <button
                                                    className="btn btn-secondary motion-download-btn"
                                                    onClick={() => downloadMotionCard(content.locale, content.languageName)}
                                                >
                                                    ⬇ Download {content.flag}
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="action-buttons">
                                <button className="btn btn-secondary" onClick={() => setStep('enhance')}>← Back</button>
                                <button className="btn btn-primary" onClick={() => setStep('publish')}>Continue to Publish →</button>
                            </div>
                        </>
                    ) : (
                        <div className="card">
                            <h3>⚠️ No content generated</h3>
                            <p>Could not generate posts. You can retry or skip to publish.</p>
                            <div className="action-buttons">
                                <button className="btn btn-secondary" onClick={handleGoToMotion}>🔄 Retry</button>
                                <button className="btn btn-primary" onClick={() => setStep('publish')}>Skip & Publish →</button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Publish Step */}
            {step === 'publish' && (
                <div className="publish-section animate-slide-up">
                    <div className="success-card card">
                        {publishStatus === 'success' ? (
                            <>
                                <span className="success-icon">✅</span>
                                <h2>Published Successfully!</h2>
                                <p>Your listing is now live on your selected channels</p>
                            </>
                        ) : (
                            <>
                                <span className="success-icon">🎉</span>
                                <h2>Your Listing is Ready!</h2>
                                <p>Choose where to publish your handmade creation</p>
                            </>
                        )}

                        <div className="publish-channels">
                            {marketplaces
                                .filter((m) => selectedMarketplaces.includes(m.id))
                                .map((mp) => (
                                    <div key={mp.id} className="publish-channel">
                                        <span className="channel-icon">{mp.icon}</span>
                                        <span className="channel-name">{mp.name}</span>
                                        <span className={`status-badge ${publishStatus === 'success' ? 'status-success' : publishStatus === 'publishing' ? 'status-publishing' : 'status-pending'}`}>
                                            {publishStatus === 'success' ? 'Published' : publishStatus === 'publishing' ? 'Publishing...' : 'Ready'}
                                        </span>
                                    </div>
                                ))}
                        </div>

                        {publishStatus !== 'success' && (
                            <button
                                className="btn btn-primary btn-large"
                                onClick={publishListing}
                                disabled={isPublishing}
                            >
                                {isPublishing ? (
                                    <>
                                        <span className="spinner">⟳</span>
                                        Publishing...
                                    </>
                                ) : (
                                    <>
                                        <span>🚀</span>
                                        Publish to All Channels
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default CreateListingPage;
